import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { StorageContext } from '../contexts/StorageContext';
import { ProjectContext } from '../contexts/ProjectContext';
import { getLanguageFromFile } from '../utils/fileUtils';
import { loadConfig } from '../utils/configUtils';
import LoadingSpinner from './LoadingSpinner';
import './CodeEditor.css';

// Lazy load the Monaco editor to reduce initial bundle size
const MonacoEditor = React.lazy(() => import('@monaco-editor/react'));

/**
 * Code editor component using Monaco editor
 */
function CodeEditor() {
  const { fileId } = useParams();
  const { storage } = useContext(StorageContext);
  const { project } = useContext(ProjectContext);
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [fileVersions, setFileVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readOnly, setReadOnly] = useState(true);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const editorRef = useRef(null);
  
  // Get editor configuration
  const config = loadConfig();
  const editorConfig = config.editor || {};
  
  // Load file and content
  useEffect(() => {
    const loadFile = async () => {
      if (!fileId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Reset state
        setUnsavedChanges(false);
        
        // Get file metadata
        const fileData = await storage.getFile(fileId);
        
        if (!fileData) {
          setError('File not found');
          return;
        }
        
        setFile(fileData);
        
        // Get all file versions
        const versions = await storage.getAllFileVersions(fileId);
        setFileVersions(versions.sort((a, b) => b.version - a.version));
        
        // Get current content
        const latestVersion = versions.sort((a, b) => b.version - a.version)[0];
        
        if (latestVersion) {
          setFileContent(latestVersion.content);
          setSelectedVersion(latestVersion.id);
        } else {
          setFileContent('');
          setError('No content available for this file');
        }
      } catch (err) {
        console.error('Error loading file:', err);
        setError('Failed to load file');
      } finally {
        setLoading(false);
      }
    };
    
    loadFile();
  }, [fileId, storage]);
  
  // Handle version change
  const handleVersionChange = async (e) => {
    // Check for unsaved changes before switching versions
    if (unsavedChanges) {
      const confirm = window.confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirm) return;
    }
    
    const versionId = e.target.value;
    setSelectedVersion(versionId);
    
    try {
      setLoading(true);
      
      // Get content for selected version
      const version = fileVersions.find(v => v.id === versionId);
      
      if (version) {
        setFileContent(version.content);
        setUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Error loading version:', err);
      setError('Failed to load version');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle editor mount
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Add keyboard shortcuts for save
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      handleSaveFile();
    });
    
    // Set initial read-only mode
    editor.updateOptions({ readOnly: readOnly });
  };
  
  // Handle content change
  const handleContentChange = (value) => {
    // Only mark as changed if the content is actually different
    if (value !== fileContent) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  };
  
  // Handle toggle edit mode
  const handleToggleEditMode = () => {
    const newMode = !readOnly;
    setReadOnly(newMode);
    
    // Update editor options
    if (editorRef.current) {
      editorRef.current.updateOptions({ readOnly: newMode });
    }
  };
  
  // Handle save file
  const handleSaveFile = async () => {
    if (readOnly || !unsavedChanges || !editorRef.current) return;
    
    try {
      setLoading(true);
      
      // Get content from editor
      const currentContent = editorRef.current.getValue();
      
      // Save file content as new version
      const currentVersion = fileVersions[0]?.version || 0;
      const contentId = await storage.saveFileContent(
        fileId,
        currentContent,
        currentVersion + 1
      );
      
      // Update file metadata
      const updatedFile = {
        ...file,
        currentVersionId: contentId,
        actualLoc: currentContent.split('\n').length
      };
      await storage.saveFile(updatedFile);
      setFile(updatedFile);
      
      // Reload versions
      const updatedVersions = await storage.getAllFileVersions(fileId);
      setFileVersions(updatedVersions.sort((a, b) => b.version - a.version));
      setSelectedVersion(contentId);
      
      // Reset unsaved changes
      setUnsavedChanges(false);
      
      // Show success message
      // Could use a toast notification here in the future
    } catch (err) {
      console.error('Error saving file:', err);
      setError('Failed to save file');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !fileContent) {
    return <div className="code-editor-loading"><LoadingSpinner message="Loading file..." /></div>;
  }
  
  if (error && !fileContent) {
    return <div className="code-editor-error">{error}</div>;
  }
  
  if (!file) {
    return <div className="code-editor-error">File not found</div>;
  }
  
  // Get language for Monaco editor
  const language = getLanguageFromFile(file.language || file.path);
  
  return (
    <div className="code-editor">
      <div className="code-editor-header">
        <div className="file-info">
          <h3>{file.path}</h3>
          <div className="file-meta">
            <span>{file.actualLoc || file.estimatedLoc} lines</span>
            <span>{language}</span>
          </div>
        </div>
        
        <div className="editor-controls">
          <div className="version-selector">
            <label htmlFor="version">Version:</label>
            <select 
              id="version" 
              value={selectedVersion || ''}
              onChange={handleVersionChange}
              disabled={fileVersions.length <= 1 || loading}
            >
              {fileVersions.map(version => (
                <option key={version.id} value={version.id}>
                  v{version.version} - {new Date(version.timestamp).toLocaleString()}
                </option>
              ))}
            </select>
          </div>
          
          <div className="editor-actions">
            <button 
              className={`edit-toggle ${!readOnly ? 'active' : ''}`}
              onClick={handleToggleEditMode}
              title={readOnly ? 'Enable editing' : 'Disable editing'}
            >
              {readOnly ? '✏️ Edit' : '👁️ View'}
            </button>
            
            <button 
              className="save-button"
              onClick={handleSaveFile}
              disabled={readOnly || !unsavedChanges || loading}
              title="Save changes"
            >
              💾 Save
            </button>
          </div>
        </div>
      </div>
      
      <div className="code-editor-container">
        {loading && <div className="editor-loading-overlay"><LoadingSpinner size="medium" /></div>}
        
        <React.Suspense fallback={<div className="code-editor-loading"><LoadingSpinner message="Loading editor..." /></div>}>
          <MonacoEditor
            height="100%"
            language={language}
            value={fileContent}
            options={{
              readOnly: readOnly,
              minimap: { enabled: editorConfig.minimap !== false },
              scrollBeyondLastLine: false,
              fontSize: editorConfig.fontSize || 14,
              wordWrap: editorConfig.wordWrap ? 'on' : 'off',
              lineNumbers: editorConfig.lineNumbers !== false ? 'on' : 'off',
              tabSize: editorConfig.tabSize || 2,
              automaticLayout: true,
              formatOnPaste: true,
              formatOnType: editorConfig.formatOnSave !== false
            }}
            theme={editorConfig.theme || 'vs-dark'}
            onChange={handleContentChange}
            onMount={handleEditorDidMount}
          />
        </React.Suspense>
      </div>
      
      {unsavedChanges && (
        <div className="unsaved-changes-banner">
          You have unsaved changes
          <button 
            className="save-changes-button"
            onClick={handleSaveFile}
            disabled={loading}
          >
            Save Now
          </button>
        </div>
      )}
    </div>
  );
}

export default CodeEditor;
